import { create } from "zustand";

export type NotificationItem = {
  id: string;
  recipientUserId?: string | null;
  recipientEmail?: string | null;
  channel?: string | null;
  notificationType?: string | null;
  title?: string | null;
  referenceNumber?: string | null;
  caseNumber?: string | null;
  message?: string | null;
  isRead: boolean;
  readAt?: string | null;
  createdOn?: string | null;
};

type NotificationState = {
  notifications: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  isStreamConnected: boolean;
  setNotifications: (
    notifications: NotificationItem[],
    unreadCount: number,
  ) => void;
  prependNotification: (notification: NotificationItem) => void;
  markNotificationRead: (notificationId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setStreamConnected: (isConnected: boolean) => void;
  clearNotifications: () => void;
};

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  isStreamConnected: false,
  setNotifications: (notifications, unreadCount) =>
    set({
      notifications,
      unreadCount,
    }),
  prependNotification: (notification) =>
    set((state) => {
      const existing = state.notifications.find((item) => item.id === notification.id);
      if (existing) {
        return {
          notifications: state.notifications.map((item) =>
            item.id === notification.id ? { ...item, ...notification } : item,
          ),
        };
      }

      return {
        notifications: [notification, ...state.notifications].slice(0, 20),
        unreadCount:
          state.unreadCount + (notification.isRead ? 0 : 1),
      };
    }),
  markNotificationRead: (notificationId) =>
    set((state) => {
      const wasUnread = state.notifications.some(
        (item) => item.id === notificationId && !item.isRead,
      );

      return {
        notifications: state.notifications.map((item) =>
          item.id === notificationId
            ? {
                ...item,
                isRead: true,
                readAt: item.readAt ?? new Date().toISOString(),
              }
            : item,
        ),
        unreadCount: wasUnread
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      };
    }),
  setLoading: (isLoading) => set({ isLoading }),
  setStreamConnected: (isStreamConnected) => set({ isStreamConnected }),
  clearNotifications: () =>
    set({
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      isStreamConnected: false,
    }),
}));
